/*
 * Small charts for Insights, drawn in SVG with no chart package: bars (one or two series side by side), a donut, a line
 * with a soft fill, and a funnel. Every mark has a hover tip, every chart with two or more series has a legend, and the
 * text stays in text colors (the mark beside it carries the color). Colors are the --chart-* tokens in app.css, checked
 * for color blindness in light and dark.
 */
import { useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { unzoom } from "../app/fit-wide";

/** The width of a box, kept up to date as the page resizes. */
function useWidth<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    setWidth(el.clientWidth);
    const ro = new ResizeObserver(([e]) => setWidth(Math.floor(e?.contentRect.width ?? 0)));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, width] as const;
}

/** A round top for the axis: 1, 2, 2.5 or 5 times a power of ten. */
function niceMax(v: number): number {
  if (v <= 0) return 1;
  const p = 10 ** Math.floor(Math.log10(v));
  for (const m of [1, 2, 2.5, 5, 10]) if (m * p >= v) return m * p;
  return 10 * p;
}

/** 15000 as 15K, for the axis only. */
export function shortNumber(v: number): string {
  const a = Math.abs(v);
  if (a >= 1_000_000) return `${String(Math.round((v / 1_000_000) * 10) / 10)}M`;
  if (a >= 1000) return `${String(Math.round((v / 1000) * 10) / 10)}K`;
  return String(Math.round(v));
}

interface Tip {
  x: number;
  y: number;
  body: ReactNode;
}
function TipBox({ tip }: { tip: Tip | null }) {
  if (!tip) return null;
  return (
    <div className="chart-tip" role="status" style={{ left: tip.x, top: tip.y }}>
      {tip.body}
    </div>
  );
}

export interface Series {
  name: string;
  values: number[];
  /** main: the brand color. soft: the brand color faded, for last year or a second look. c1 to c5: one per company. */
  tone: "main" | "soft" | "alt" | "c1" | "c2" | "c3" | "c4" | "c5";
  /** A color of its own (#RRGGBB), over the tone: a company's brand color. */
  color?: string | undefined;
}

export function Legend({ items }: { items: { name: string; tone: string; color?: string | undefined; value?: string }[] }) {
  return (
    <ul className="chart-legend">
      {items.map((i) => (
        <li key={i.name}>
          <span className={`chart-dot ${i.tone}`} style={i.color ? { background: i.color } : undefined} aria-hidden="true" />
          {i.name}
          {i.value ? <b className="num">{i.value}</b> : null}
        </li>
      ))}
    </ul>
  );
}

/** A bar from yTop down to the base, its top corners rounded by r. */
function barPath(x: number, w: number, yTop: number, yBase: number, r: number): string {
  return `M${x},${yBase} V${yTop + r} Q${x},${yTop} ${x + r},${yTop} H${x + w - r} Q${x + w},${yTop} ${x + w},${yTop + r} V${yBase} Z`;
}

/**
 * Bars by label, one or more series side by side. format turns a value into words for the tip; the axis uses short
 * numbers. total: the tip lists only the series above zero, most first, with a Total line; the hovered label's bars
 * light up while the others fade; and with more than one series each label's total sits in small type above its bars.
 */
export function BarChart({
  labels,
  tipLabels,
  series,
  format,
  height = 200,
  axisPrefix = "",
  total = false,
}: {
  labels: string[];
  tipLabels?: string[];
  series: Series[];
  format: (v: number) => string;
  height?: number;
  axisPrefix?: string;
  total?: boolean;
}) {
  const [ref, width] = useWidth<HTMLDivElement>();
  const [tip, setTip] = useState<Tip | null>(null);
  const [hot, setHot] = useState<number | null>(null);
  const left = 44;
  const bottom = 24;
  const labelled = total && series.length > 1;
  const top = labelled ? 22 : 8; // room for the totals above the tallest bars
  const plotW = Math.max(0, width - left - 4);
  const plotH = height - bottom - top;
  const sums = labels.map((_, i) => series.reduce((a, s) => a + (s.values[i] ?? 0), 0));
  const max = niceMax(Math.max(0, ...series.flatMap((s) => s.values)));
  const ticks = [0, max / 2, max];
  const slot = labels.length ? plotW / labels.length : 0;
  const gap = 2;
  const groupW = Math.min(slot * 0.72, 28 * series.length + gap * (series.length - 1));
  const barW = series.length ? Math.max(2, (groupW - gap * (series.length - 1)) / series.length) : 0;
  const y = (v: number) => top + plotH - (v / max) * plotH;
  // Show every label when they fit, else every second or third.
  const every = slot >= 44 ? 1 : slot >= 22 ? 2 : 3;
  return (
    <div className="chart" ref={ref} onMouseLeave={() => setTip(null)}>
      {width > 0 ? (
        <svg width={width} height={height} role="img" aria-label={series.map((s) => s.name).join(" and ")}>
          {ticks.map((t) => (
            <g key={t}>
              <line className="chart-grid" x1={left} x2={width - 4} y1={y(t)} y2={y(t)} />
              <text className="chart-axis" x={left - 8} y={y(t)} dy="0.32em" textAnchor="end">
                {t === 0 ? "0" : `${axisPrefix}${shortNumber(t)}`}
              </text>
            </g>
          ))}
          {labels.map((label, i) => {
            const x0 = left + i * slot + (slot - groupW) / 2;
            const at = (s: Series) => s.values[i] ?? 0;
            const inTip = total ? series.filter((s) => at(s) > 0).sort((a, b) => at(b) - at(a)) : series;
            return (
              <g key={`${label}-${String(i)}`} className={hot === null ? undefined : hot === i ? "chart-hot" : "chart-dim"}>
                {labelled && (sums[i] ?? 0) > 0 ? (
                  <text
                    className="chart-total-label"
                    x={x0 + groupW / 2}
                    y={y(Math.max(...series.map(at))) - 6}
                    textAnchor="middle"
                  >
                    {`${axisPrefix}${shortNumber(sums[i] ?? 0)}`}
                  </text>
                ) : null}
                {series.map((s, k) => {
                  const v = at(s);
                  const h = Math.max(0, y(0) - y(v));
                  const x = x0 + k * (barW + gap);
                  return (
                    <path
                      key={s.name}
                      className={`chart-bar ${s.tone}`}
                      style={s.color ? { fill: s.color } : undefined}
                      d={h <= 0 ? "" : barPath(x, barW, y(v), y(0), Math.min(4, barW / 2, h))}
                    />
                  );
                })}
                {/* The hover target is the whole slot, bigger than the bars. */}
                <rect
                  x={left + i * slot}
                  y={top}
                  width={slot}
                  height={plotH}
                  fill="transparent"
                  onMouseLeave={() => setHot(null)}
                  onMouseEnter={() => {
                    setHot(total ? i : null);
                    setTip({
                      x: Math.min(Math.max(left + i * slot + slot / 2, 90), width - 90),
                      y: top + 4,
                      body: (
                        <>
                          <b>{tipLabels?.[i] ?? label}</b>
                          {inTip.map((s) => (
                            <span key={s.name}>
                              <span
                                className={`chart-dot ${s.tone}`}
                                style={s.color ? { background: s.color } : undefined}
                                aria-hidden="true"
                              />{" "}
                              {s.name} <b className="num">{format(at(s))}</b>
                            </span>
                          ))}
                          {total ? (
                            <span className="chart-tip-total">
                              {inTip.length ? "Total" : "Nothing collected"}{" "}
                              <b className="num">{format(series.reduce((a, s) => a + at(s), 0))}</b>
                            </span>
                          ) : null}
                        </>
                      ),
                    });
                  }}
                />
                {i % every === 0 ? (
                  <text className="chart-axis" x={left + i * slot + slot / 2} y={height - 6} textAnchor="middle">
                    {label}
                  </text>
                ) : null}
              </g>
            );
          })}
          <line className="chart-base" x1={left} x2={width - 4} y1={y(0)} y2={y(0)} />
        </svg>
      ) : (
        <div style={{ height }} />
      )}
      <TipBox tip={tip} />
    </div>
  );
}

/** A line with a soft fill under it, for a percent over time. */
export function LineChart({
  labels,
  values,
  format,
  height = 180,
  max: fixedMax,
}: {
  labels: string[];
  values: (number | null)[];
  format: (v: number) => string;
  height?: number;
  max?: number;
}) {
  const [ref, width] = useWidth<HTMLDivElement>();
  const [hover, setHover] = useState<number | null>(null);
  const left = 44;
  const bottom = 24;
  const top = 10;
  const plotW = Math.max(0, width - left - 12);
  const plotH = height - bottom - top;
  const max = fixedMax ?? niceMax(Math.max(0, ...values.map((v) => v ?? 0)));
  const step = labels.length > 1 ? plotW / (labels.length - 1) : 0;
  const x = (i: number) => left + (labels.length > 1 ? i * step : plotW / 2);
  const y = (v: number) => top + plotH - (v / max) * plotH;
  const pts = values.map((v, i) => (v === null ? null : ([x(i), y(v)] as const)));
  const drawn = pts.filter((p): p is readonly [number, number] => p !== null);
  const line = drawn.map((p, i) => `${i ? "L" : "M"}${p[0]},${p[1]}`).join(" ");
  const first = drawn[0];
  const last = drawn[drawn.length - 1];
  const area = first && last ? `${line} L${last[0]},${y(0)} L${first[0]},${y(0)} Z` : "";
  const every = Math.max(1, Math.ceil(labels.length / Math.max(1, Math.floor(plotW / 60))));
  const h = hover === null ? null : values[hover];
  return (
    <div className="chart" ref={ref} onMouseLeave={() => setHover(null)}>
      {width > 0 ? (
        <svg
          width={width}
          height={height}
          role="img"
          aria-label="Line chart"
          onMouseMove={(e) => {
            if (!labels.length) return;
            const bx = e.currentTarget.getBoundingClientRect().left;
            const i = Math.round((unzoom(e.clientX - bx) - left) / (step || 1));
            setHover(Math.max(0, Math.min(labels.length - 1, i)));
          }}
        >
          {[0, max / 2, max].map((t) => (
            <g key={t}>
              <line className="chart-grid" x1={left} x2={width - 12} y1={y(t)} y2={y(t)} />
              <text className="chart-axis" x={left - 8} y={y(t)} dy="0.32em" textAnchor="end">
                {format(t)}
              </text>
            </g>
          ))}
          <path className="chart-area" d={area} />
          <path className="chart-line" d={line} />
          {drawn.length === 1 && first ? <circle className="chart-point" cx={first[0]} cy={first[1]} r={4} /> : null}
          {labels.map((l, i) =>
            i % every === 0 ? (
              <text key={`${l}-${String(i)}`} className="chart-axis" x={x(i)} y={height - 6} textAnchor="middle">
                {l}
              </text>
            ) : null,
          )}
          {hover !== null ? (
            <>
              <line className="chart-cross" x1={x(hover)} x2={x(hover)} y1={top} y2={y(0)} />
              {h != null ? <circle className="chart-point" cx={x(hover)} cy={y(h)} r={4} /> : null}
            </>
          ) : null}
        </svg>
      ) : (
        <div style={{ height }} />
      )}
      <TipBox
        tip={
          hover === null
            ? null
            : {
                x: Math.min(x(hover), width - 90),
                y: top,
                body: (
                  <>
                    <b>{labels[hover]}</b>
                    <span>{h == null ? "Nothing sent" : format(h)}</span>
                  </>
                ),
              }
        }
      />
    </div>
  );
}

const CAT = ["c1", "c2", "c3", "c4", "c5"] as const;

/** A donut with the total in the middle and a legend with each part's value and share. Past 5 parts, the rest is Other. */
export function Donut({
  parts,
  format,
  totalLabel = "Total",
  tones,
}: {
  parts: { name: string; value: number }[];
  format: (v: number) => string;
  totalLabel?: string;
  /** Fixed colors by part name, so a part keeps its color when others come and go. */
  tones?: Record<string, string>;
}) {
  const [hover, setHover] = useState<string | null>(null);
  const sorted = [...parts].filter((p) => p.value > 0).sort((a, b) => b.value - a.value);
  const shown =
    sorted.length > 5
      ? [...sorted.slice(0, 4), { name: "Other", value: sorted.slice(4).reduce((a, p) => a + p.value, 0) }]
      : sorted;
  const total = shown.reduce((a, p) => a + p.value, 0);
  const size = 168;
  const r = 70;
  const stroke = 18;
  const c = 2 * Math.PI * r;
  const gap = shown.length > 1 ? 2 : 0;
  let at = 0;
  const toneOf = (name: string, i: number) => tones?.[name] ?? CAT[i] ?? "c5";
  const focus = shown.find((p) => p.name === hover);
  return (
    <div className="donut-wrap">
      <svg width={size} height={size} viewBox={`0 0 ${String(size)} ${String(size)}`} role="img" aria-label={totalLabel}>
        <g transform={`rotate(-90 ${String(size / 2)} ${String(size / 2)})`}>
          {total === 0 ? (
            <circle className="donut-empty" cx={size / 2} cy={size / 2} r={r} strokeWidth={stroke} fill="none" />
          ) : (
            shown.map((p, i) => {
              const len = (p.value / total) * c;
              const seg = (
                <circle
                  key={p.name}
                  className={`donut-part ${toneOf(p.name, i)}${hover && hover !== p.name ? " dim" : ""}`}
                  cx={size / 2}
                  cy={size / 2}
                  r={r}
                  fill="none"
                  strokeWidth={stroke}
                  strokeDasharray={`${String(Math.max(0, len - gap))} ${String(c)}`}
                  strokeDashoffset={-at}
                  onMouseEnter={() => setHover(p.name)}
                  onMouseLeave={() => setHover(null)}
                />
              );
              at += len;
              return seg;
            })
          )}
        </g>
        <text className="donut-total" x="50%" y="48%" textAnchor="middle">
          {format(focus ? focus.value : total)}
        </text>
        <text className="donut-label" x="50%" y="62%" textAnchor="middle">
          {focus ? focus.name : totalLabel}
        </text>
      </svg>
      <ul className="chart-legend donut-legend">
        {shown.map((p, i) => (
          <li key={p.name} onMouseEnter={() => setHover(p.name)} onMouseLeave={() => setHover(null)}>
            <span className={`chart-dot ${toneOf(p.name, i)}`} aria-hidden="true" />
            <span className="donut-name">{p.name}</span>
            <b className="num">{format(p.value)}</b>
            <span className="num muted">{total ? `${String(Math.round((p.value / total) * 100))}%` : ""}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Steps that get smaller, like leads, then quotes sent, then won. Each bar is its share of the first step. */
export function Funnel({ steps }: { steps: { name: string; value: number }[] }) {
  const first = steps[0]?.value ?? 0;
  return (
    <ol className="funnel">
      {steps.map((s, i) => {
        const share = first > 0 ? s.value / first : 0;
        const prev = i > 0 ? (steps[i - 1]?.value ?? 0) : 0;
        return (
          <li key={s.name}>
            <div className="funnel-head">
              <span>{s.name}</span>
              <b className="num">{s.value}</b>
            </div>
            <div className="funnel-track">
              <div className="funnel-bar" style={{ width: `${String(Math.max(share * 100, s.value ? 2 : 0))}%` }} />
            </div>
            {i > 0 ? (
              <span className="muted small">
                {prev > 0 ? `${String(Math.round((s.value / prev) * 100))}% of the step before` : "None before"}
              </span>
            ) : null}
          </li>
        );
      })}
    </ol>
  );
}

/** Up or down against the days before, as a small chip. lowerIsBetter flips which way is good. */
export function Change({ now, before, lowerIsBetter = false }: { now: number; before: number; lowerIsBetter?: boolean }) {
  if (before === 0 && now === 0) return <span className="change flat">No change</span>;
  if (before === 0) return <span className="change flat">New</span>;
  const pct = Math.round(((now - before) / Math.abs(before)) * 100);
  if (pct === 0) return <span className="change flat">0%</span>;
  const up = pct > 0;
  const good = up !== lowerIsBetter;
  return (
    <span className={`change ${good ? "good" : "bad"}`}>
      <span aria-hidden="true">{up ? "↑" : "↓"}</span> {String(Math.abs(pct))}%
      <span className="sr-only">{up ? " up" : " down"} from the days before</span>
    </span>
  );
}
